import { useUpdateEffect } from '@cian/react-utils';
import { Tabs } from '@cian/ui-kit';

import { type FC, type JSX, useCallback, useMemo, useRef, useState } from 'react';

import { useMortgageCalculatorContext } from '../../contexts/MortgageCalculatorContext';
import { EMortgageOffersTabs } from '../../enums/EMortgageOffersTabs';
import { type IPromo } from '../../types/promos';
import { AccreditedBanksContainer } from '../AccreditedBanks';
import { MortgageOffersContainer } from '../MortgageOffers';

import { MortgageSection } from './MortgageSection';
import { trackMortgageOffersTabClick } from './internal/tracking/trackMortgageOffersTabClick';
import { trackPromoOffersShow } from './internal/tracking/trackPromoOffersShow';
import { trackShowMortgageCalculatorSection } from './internal/tracking/trackShowMortgageCalculatorSection';
import { getInitialTab, getSectionTabs, getSectionTitle } from './utils';

export const MortgageSectionContainer: FC = () => {
  const { banks, mortgagePromos } = useMortgageCalculatorContext();

  const hasBanks = !!banks.length;
  const hasMortgagePromos = !!mortgagePromos.length;

  const { tabs, title } = useMemo(
    () => ({
      tabs: getSectionTabs({ hasBanks, hasMortgagePromos }),
      title: getSectionTitle({ hasBanks, hasMortgagePromos }),
    }),
    [hasBanks, hasMortgagePromos],
  );

  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(() => getInitialTab(tabs));

  useUpdateEffect(() => {
    setActiveTabIndex(getInitialTab(tabs));
  }, [tabs]);

  const displayedOffersRef = useRef<IPromo[]>([]);

  const handleDisplayedOffersChange = useCallback<(displayedOffers: IPromo[]) => void>(displayedOffers => {
    displayedOffersRef.current = displayedOffers;
  }, []);

  const handleShow = useCallback<VoidFunction>(() => {
    trackShowMortgageCalculatorSection();
    trackPromoOffersShow(displayedOffersRef.current);
  }, []);

  const handleTabClick = useCallback<(index: number) => void>(index => {
    setActiveTabIndex(index);
    trackMortgageOffersTabClick(tabs[index].value);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTabsNodeVisible = tabs.length > 1;

  if (activeTabIndex == null || tabs[activeTabIndex] == null) {
    return null;
  }

  return (
    <MortgageSection
      tabs={
        isTabsNodeVisible && (
          <Tabs
            activeTabIndex={activeTabIndex}
            dataTestId="mortgage-section-tabs"
            items={tabs}
            onTabClick={handleTabClick}
          />
        )
      }
      title={title}
      onView={handleShow}
    >
      {((): JSX.Element => {
        switch (tabs[activeTabIndex].value) {
          case EMortgageOffersTabs.Mortgage:
            return <MortgageOffersContainer onDisplayedOffersChange={handleDisplayedOffersChange} />;
          case EMortgageOffersTabs.Banks:
            return <AccreditedBanksContainer />;
        }
      })()}
    </MortgageSection>
  );
};
